import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
  DOWNLOAD_FORMAT_TYPES,
  ReportType,
  REPORTS_STATE_TYPES,
  SUMMARY_REPORT,
  SUMMARY_REPORT_TYPES,
} from "constants/bookkeeping";
import { CompareByType, VarianceType } from "constants/reports";
import { Pagination } from "types/Models/pagination";
import {
  OtherItemType,
  PeriodType,
  PeriodValue,
} from "constants/revenueMetrics";
import { ReportsType } from "pages/Books/Reports/Reports";
import qs from "qs";
import { GeneralLedger } from "types/Models/books";
import { FileObject, FileUrl } from "types/Models/fileObject";
import {
  ARAgingSummary,
  ForecastReportByView,
  ForecastRuleInput,
  ForecastRule,
  ForecastSettings,
  InteractiveReport,
  ReportByView,
  SelectedTransactionExportRow,
  SelectedTransactionsExportResponse,
  VarianceExplanation,
  VarianceExplanationList,
} from "types/Models/report";
import { emptyApi } from "./emptyApi";

export type Report = {
  uuid: string;
  state: REPORTS_STATE_TYPES;
  entity: string;
  start_date: null | string;
  end_date: null | string;
  show_non_zero_accounts?: boolean;
  created_at: string;
  type: ReportsType;
  report: null | FileObject;
  closing_month?: {
    season_name: string;
    date: string;
    end_date: string;
  };
};

export type InteractiveNewReportParams = {
  reportType: string;
  accountingMethod?: ACCOUNTING_METHOD_TYPES;
  startDate?: string | null;
  endDate?: string | null;
  showNonZeroAccounts?: boolean | null;
  action?: DOWNLOAD_FORMAT_TYPES;
  comparison_report_type?:
    | PeriodType
    | "CLASSIFICATION"
    | OtherItemType
    | CompareByType
    | null;
  variance_type?: VarianceType[] | null;
  export_to_mail?: boolean;
  draft_watermark?: boolean;
  transaction_group_id?: string | null;
  transaction_group_ids?: string | null;
  class_ids?: string[];
  fetch_ungrouped_txn_ids?: string | null;
  folderId?: string;
};

export type VendorSummaryPeriod = Record<
  string,
  {
    amount: number;
    start_date: string;
    end_date: string;
  } | null
>;

export type VendorSummaryCategoryList = {
  name: string;
  category_id: string;
  period: VendorSummaryPeriod;
};

export type VendorSummaryVendorList = {
  name: string;
  vendor_id: string;
  logo?: string | null;
  period: VendorSummaryPeriod;
};

export type VendorSummaryItem =
  | {
      name: string;
      vendor_id: string;
      is_related_party?: boolean;
      category_list: VendorSummaryCategoryList[];
    }
  | {
      name: string;
      category_id: string;
      vendor_list: VendorSummaryVendorList[];
    };

/** @deprecated Use VendorSummaryItem instead */
export type VendorSummary = {
  name: string;
  category: {
    name: string;
    category_id: string;
    period: VendorSummaryPeriod;
  };
};

export type ConsolidatedReportDownloadRequest = {
  report_type: ReportType;
  comparison_report_type?: string | null;
  class_ids?: string[] | null;
  days_per_aging_period?: number;
  number_of_periods?: number;
  show_deferrals?: boolean;
  include_carry_forward?: boolean;
  custom_report_id?: string;
};

export type ConsolidatedReportExportPayload = {
  start_date: string;
  end_date: string;
  accounting_method: string;
  show_non_zero_accounts: boolean;
  folder_id?: string;
  export_to_mail?: boolean;
  action: string;
  download_requests: ConsolidatedReportDownloadRequest[];
  financial_summary_graphs?: string[];
};

export const interactiveReportFilterValuesToParams = (
  values: InteractiveNewReportParams
): string => {
  return qs.stringify(
    {
      report_type: values.reportType,
      start_date: values.startDate,
      end_date: values.endDate,
      accounting_method: values.accountingMethod,
      show_non_zero_accounts: values.showNonZeroAccounts,
      action: values.action,
      comparison_report_type: values.comparison_report_type,
      variance_type: values.variance_type?.join(","),
      export_to_mail: values.export_to_mail,
      draft_watermark: values.draft_watermark,
      transaction_group_id: values.transaction_group_id,
      transaction_group_ids: values.transaction_group_ids,
      class_ids: values.class_ids?.join(",") || null,
      fetch_ungrouped_txn_ids: values.fetch_ungrouped_txn_ids,
      folder_id: values.folderId,
    },
    { skipNulls: true, addQueryPrefix: true }
  );
};

export const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllReports: build.query<Report[], { groupId: string; entityId: string }>(
      {
        query: ({ groupId, entityId }) => {
          return {
            url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/report/`,
          };
        },
        providesTags: ["REPORTS"],
      }
    ),

    getReportById: build.query<
      Report,
      { groupId: string; entityId: string; reportId: string }
    >({
      query: ({ groupId, entityId, reportId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/report/${reportId}/`,
        };
      },
      providesTags: ["REPORTS"],
    }),

    generateReport: build.mutation<
      Report,
      {
        groupId: string;
        entityId: string;
        reportType: ReportsType;
      }
    >({
      query: ({ groupId, entityId, reportType }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/report/`,
          method: "POST",
          body: {
            report_type: reportType,
          },
        };
      },
      invalidatesTags: ["REPORTS"],
    }),

    updateReport: build.mutation<
      Report,
      {
        groupId: string;
        entityId: string;
        reportId: string;
        endDate: string;
        startDate: string;
        showNonZero: boolean;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportId,
        endDate,
        startDate,
        showNonZero,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/report/${reportId}/`,
          method: "PUT",
          body: {
            end_date: endDate,
            start_date: startDate,
            show_non_zero_accounts: showNonZero,
          },
        };
      },
      invalidatesTags: ["REPORTS"],
    }),

    deleteReportFile: build.mutation<
      Report,
      {
        groupId: string;
        entityId: string;
        reportId: string;
      }
    >({
      query: ({ groupId, entityId, reportId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/report/${reportId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["REPORTS"],
    }),

    newInteractiveReport: build.query<
      InteractiveReport,
      {
        groupId: string;
        entityId: string;
      } & InteractiveNewReportParams
    >({
      query: ({ groupId, entityId, ...params }) => {
        let queryUrl = interactiveReportFilterValuesToParams(params);
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/new/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["LIVE_REPORT"] : []),
    }),

    exportSelectedTransactions: build.mutation<
      SelectedTransactionsExportResponse,
      {
        groupId: string;
        entityId: string;
        transactions: SelectedTransactionExportRow[];
        exportToMail?: boolean;
        folderId?: string;
      }
    >({
      query: ({ groupId, entityId, transactions, exportToMail, folderId }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/export_selected_transactions/`,
        method: "POST",
        body: {
          transactions,
          export_to_mail: exportToMail || false,
          folder_id: folderId || null,
        },
      }),
    }),

    byViewInteractiveReport: build.query<
      ReportByView,
      {
        groupId: string;
        entityId: string;
        reportType: string;
        accountingMethod?: string;
        startDate?: string | null;
        endDate?: string | null;
        showNonZeroAccounts?: boolean | null;
        comparison_report_type:
          | PeriodType
          | "CLASSIFICATION"
          | OtherItemType
          | CompareByType
          | null;
        transaction_group_id?: string | null;
        transaction_group_ids?: string | null;
        class_ids?: string[];
        fetch_ungrouped_txn_ids?: string | null;
        useCachedReport?: boolean;
        isVarianceReport?: boolean;
        // Client-only cache-buster: bumped on every "Regenerate" click so RTK Query
        // treats it as a fresh request instead of serving the cached result. Never
        // sent to the backend — intentionally excluded from the querystring below.
        regenerateCount?: number;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        accountingMethod,
        startDate,
        endDate,
        showNonZeroAccounts,
        comparison_report_type,
        transaction_group_id,
        transaction_group_ids,
        fetch_ungrouped_txn_ids,
        class_ids,
        useCachedReport,
        isVarianceReport,
      }) => {
        let queryUrl = qs.stringify(
          {
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            accounting_method: accountingMethod,
            show_non_zero_accounts: showNonZeroAccounts,
            comparison_report_type,
            transaction_group_id: transaction_group_id,
            transaction_group_ids: transaction_group_ids,
            class_ids:
              class_ids && class_ids.length > 0 ? class_ids.join(",") : null,
            fetch_ungrouped_txn_ids: fetch_ungrouped_txn_ids,
            use_cached_report: useCachedReport,
            is_variance_report: isVarianceReport,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/new/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["LIVE_REPORT"] : []),
    }),

    getVarianceExplanation: build.query<
      VarianceExplanation,
      {
        groupId: string;
        entityId: string;
        categoryUuid?: string | null;
        syntheticRowName?: string | null;
        reportType: string;
        startDate?: string | null;
        endDate?: string | null;
        accountingMethod?: string;
        comparisonReportType: CompareByType;
        showNonZeroAccounts?: boolean | null;
        forceRegenerate?: boolean;
        // Client-only cache-buster: bumped on every "Regenerate" click so RTK Query
        // treats it as a fresh request instead of serving the cached result. Never
        // sent to the backend — intentionally excluded from the querystring below.
        regenerateCount?: number;
      }
    >({
      query: ({
        groupId,
        entityId,
        categoryUuid,
        syntheticRowName,
        reportType,
        startDate,
        endDate,
        accountingMethod,
        comparisonReportType,
        showNonZeroAccounts,
        forceRegenerate,
      }) => {
        let queryUrl = qs.stringify(
          {
            category_uuid: categoryUuid,
            synthetic_row_name: syntheticRowName,
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            accounting_method: accountingMethod,
            comparison_report_type: comparisonReportType,
            show_non_zero_accounts: showNonZeroAccounts,
            force_regenerate: forceRegenerate,
            is_variance_report: true,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/variance_explanation/${queryUrl}`,
        };
      },
    }),

    getVarianceExplanationList: build.query<
      VarianceExplanationList,
      {
        groupId: string;
        entityId: string;
        reportType: string;
        startDate?: string | null;
        endDate?: string | null;
        accountingMethod?: string;
        comparisonReportType: CompareByType;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        startDate,
        endDate,
        accountingMethod,
        comparisonReportType,
      }) => {
        let queryUrl = qs.stringify(
          {
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            accounting_method: accountingMethod,
            comparison_report_type: comparisonReportType,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/variance_explanation/list/${queryUrl}`,
        };
      },
      providesTags: ["VARIANCE_EXPLANATION"],
    }),

    exportVarianceExplanationReport: build.mutation<
      { document_id: string },
      {
        groupId: string;
        entityId: string;
        reportType: string;
        startDate?: string | null;
        endDate?: string | null;
        accountingMethod?: string;
        comparisonReportType: CompareByType;
        showNonZeroAccounts?: boolean | null;
        categoryUuidsForVendorTab: string[];
        folderId?: string | null;
        exportToMail?: boolean;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        startDate,
        endDate,
        accountingMethod,
        comparisonReportType,
        showNonZeroAccounts,
        categoryUuidsForVendorTab,
        folderId,
        exportToMail,
      }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/variance_explanation/export/`,
        method: "POST",
        body: {
          report_type: reportType,
          start_date: startDate,
          end_date: endDate,
          accounting_method: accountingMethod,
          comparison_report_type: comparisonReportType,
          show_non_zero_accounts: showNonZeroAccounts,
          category_uuids_for_vendor_tab: categoryUuidsForVendorTab,
          folder_id: folderId || null,
          export_to_mail: exportToMail || false,
          is_variance_report: true,
        },
      }),
    }),

    getTransactionsForReport: build.query<
      Omit<GeneralLedger, "types"> & Partial<Pagination>,
      {
        entityId: string;
        query: {
          start_date?: string | null;
          end_date?: string | null;
          report_type?: string;
          category_id: string;
          accounting_method?: string | null;
          vendor_name?: string | null;
          transaction_group_id?: string | null;
          transaction_group_ids?: string | null;
          class_id?: string | null;
          fetch_ungrouped_txn_ids?: string | null;
          class_ids?: string | null;
          merchant_id?: string | null;
          page_num?: number;
          page_size?: number;
        };
      }
    >({
      query: ({ entityId, query }) => {
        let queryUrl = qs.stringify(query, {
          skipNulls: true,
          addQueryPrefix: true,
        });

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/report-txns/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["BOOKS_TRANSACTIONS"] : []),
    }),

    saveInteractiveReport: build.mutation<
      InteractiveReport,
      {
        groupId: string;
        entityId: string;
        reportType: string;
        accountingMethod?: string;
        startDate?: string | null;
        endDate?: string | null;
        showNonZeroAccounts?: boolean | null;
        comparison_report_type: PeriodType;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        accountingMethod,
        startDate,
        endDate,
        showNonZeroAccounts,
        comparison_report_type,
      }) => {
        let queryUrl = qs.stringify(
          {
            report_type: reportType,
            accounting_method: accountingMethod,
            start_date: startDate,
            end_date: endDate,
            show_non_zero_accounts: showNonZeroAccounts,
            comparison_report_type: comparison_report_type,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/${queryUrl}`,
          method: "POST",
        };
      },
      invalidatesTags: (result) => (result ? ["INTERACTIVE_REPORTS"] : []),
    }),

    getAllInteractiveReports: build.query<
      { reports: InteractiveReport[] },
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/`,
        };
      },
      providesTags: (result) => (result ? ["INTERACTIVE_REPORTS"] : []),
    }),

    updateInteractiveReport: build.mutation<
      InteractiveReport,
      {
        groupId: string;
        entityId: string;
        reportId: string;
        payload: { report_name: string };
      }
    >({
      query: ({ groupId, entityId, reportId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/${reportId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) => (result ? ["INTERACTIVE_REPORTS"] : []),
    }),

    getInteractiveReport: build.query<
      InteractiveReport | ReportByView,
      {
        groupId: string;
        entityId: string;
        reportId: string;
      }
    >({
      query: ({ groupId, entityId, reportId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/${reportId}/`,
        };
      },
      providesTags: (result) => (result ? ["INTERACTIVE_REPORTS"] : []),
    }),

    deleteInteractiveReport: build.mutation<
      InteractiveReport,
      {
        groupId: string;
        entityId: string;
        reportId: string;
      }
    >({
      query: ({ groupId, entityId, reportId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/${reportId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) => (result ? ["INTERACTIVE_REPORTS"] : []),
    }),

    getAgingSummary: build.query<
      { ar_aging_summary: ARAgingSummary[] },
      {
        entityId: string;
        date: string;
        accountingMethod?: string;
        daysPerAgingPeriod?: number;
        numberOfAgingPeriods?: number;
      }
    >({
      query: ({
        entityId,
        date,
        accountingMethod,
        daysPerAgingPeriod,
        numberOfAgingPeriods,
      }) => {
        let queryUrl = qs.stringify(
          {
            date: date,
            accounting_method: accountingMethod,
            days_per_aging_period: daysPerAgingPeriod,
            number_of_periods: numberOfAgingPeriods,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ar-aging-summary/${queryUrl}`,
          method: "GET",
        };
      },
      providesTags: (result) => (result ? ["AGING_SUMMARY"] : []),
    }),

    getAPAgingSummary: build.query<
      { ap_aging_summary: ARAgingSummary[] },
      {
        entityId: string;
        date: string;
        accountingMethod?: string;
        daysPerAgingPeriod?: number;
        numberOfAgingPeriods?: number;
      }
    >({
      query: ({
        entityId,
        date,
        accountingMethod,
        daysPerAgingPeriod,
        numberOfAgingPeriods,
      }) => {
        let queryUrl = qs.stringify(
          {
            date: date,
            accounting_method: accountingMethod,
            days_per_aging_period: daysPerAgingPeriod,
            number_of_periods: numberOfAgingPeriods,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ap-aging-summary/${queryUrl}`,
          method: "GET",
        };
      },
      providesTags: (result) => (result ? ["AGING_SUMMARY"] : []),
    }),

    exportARAgingSummary: build.mutation<
      string,
      {
        entityId: string;
        date: string;
        accountingMethod?: string;
        daysPerAgingPeriod?: number;
        numberOfAgingPeriods?: number;
        export_to_mail?: boolean;
        folderId?: string;
      }
    >({
      query: ({
        entityId,
        date,
        accountingMethod,
        daysPerAgingPeriod,
        numberOfAgingPeriods,
        export_to_mail,
        folderId,
      }) => {
        let queryUrl = qs.stringify(
          {
            date: date,
            accounting_method: accountingMethod,
            days_per_aging_period: daysPerAgingPeriod,
            number_of_periods: numberOfAgingPeriods,
            export_to_mail: export_to_mail,
            folder_id: folderId,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ar-aging-summary/export${queryUrl}`,
          method: "POST",
        };
      },
    }),

    exportAPAgingSummary: build.mutation<
      string,
      {
        entityId: string;
        date: string;
        accountingMethod?: string;
        daysPerAgingPeriod?: number;
        numberOfAgingPeriods?: number;
        export_to_mail?: boolean;
        folderId?: string;
      }
    >({
      query: ({
        entityId,
        date,
        accountingMethod,
        daysPerAgingPeriod,
        numberOfAgingPeriods,
        export_to_mail,
        folderId,
      }) => {
        let queryUrl = qs.stringify(
          {
            date: date,
            accounting_method: accountingMethod,
            days_per_aging_period: daysPerAgingPeriod,
            number_of_periods: numberOfAgingPeriods,
            export_to_mail: export_to_mail,
            folder_id: folderId,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ap-aging-summary/export${queryUrl}`,
          method: "POST",
        };
      },
    }),
    getVendorSummary: build.query<
      VendorSummaryItem[],
      {
        entityId: string;
        startDate: string;
        endDate: string;
        reportType: PeriodType;
        accountingMethod?: keyof typeof ACCOUNTING_METHODS;
        vendorNames: string[];
        comparisonSummaryType: keyof typeof SUMMARY_REPORT;
        categoryList: string[];
        isRelatedParty?: boolean;
      }
    >({
      query: ({
        entityId,
        startDate,
        endDate,
        reportType,
        accountingMethod,
        vendorNames,
        comparisonSummaryType,
        categoryList,
        isRelatedParty,
      }) => {
        let queryUrl = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            report_type: reportType,
            accounting_method: accountingMethod,
            vendor_names: vendorNames.length > 0 ? vendorNames.join(",") : null,
            comparison_summary_type: comparisonSummaryType,
            category_list:
              categoryList.length > 0 ? categoryList.join(",") : null,
            is_related_party: isRelatedParty || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/vendor_summary/${queryUrl}`,
        };
      },
      providesTags: [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "VENDORS",
        "TRANSACTION_MERCHANTS",
        "VENDOR_SUMMARY",
      ],
    }),
    exportVendorSummary: build.query<
      FileUrl & { document_id: string },
      {
        entityId: string;
        startDate: string;
        endDate: string;
        reportType: PeriodType;
        accountingMethod?: keyof typeof ACCOUNTING_METHODS;
        comparisonSummaryType?: SUMMARY_REPORT_TYPES;
        categoryList: string[];
        vendorNames: string[];
        export_to_mail?: boolean;
        folderId?: string;
        isRelatedParty?: boolean;
      }
    >({
      query: ({
        entityId,
        startDate,
        endDate,
        reportType,
        accountingMethod,
        comparisonSummaryType,
        categoryList,
        vendorNames,
        export_to_mail,
        folderId,
        isRelatedParty,
      }) => {
        let queryUrl = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            report_type: reportType,
            accounting_method: accountingMethod,
            comparison_summary_type: comparisonSummaryType,
            vendor_names: vendorNames.length > 0 ? vendorNames.join(",") : null,
            category_list:
              categoryList.length > 0 ? categoryList.join(",") : null,
            report_format: "xlsx",
            export_to_mail: export_to_mail,
            folder_id: folderId,
            is_related_party: isRelatedParty || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/download_vendor_summary/${queryUrl}`,
        };
      },
      providesTags: ["VENDOR_SUMMARY"],
    }),

    exportConsolidatedReport: build.mutation<
      { document_id?: string },
      {
        entityId: string;
        payload: ConsolidatedReportExportPayload;
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/consolidated-report-export/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    getForecastReport: build.query<
      ForecastReportByView,
      {
        groupId: string;
        entityId: string;
        reportType: string;
        startDate: string;
        endDate: string;
        forecastStartDate: string;
        forecastEndDate: string;
        assumptionSetId?: string | null;
        accountingMethod?: string;
        showNonZeroAccounts?: boolean | null;
        viewBy?: string | null;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        startDate,
        endDate,
        forecastStartDate,
        forecastEndDate,
        assumptionSetId,
        accountingMethod,
        showNonZeroAccounts,
        viewBy,
      }) => {
        const queryUrl = qs.stringify(
          {
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            forecast_start_date: forecastStartDate,
            forecast_end_date: forecastEndDate,
            assumption_set_id: assumptionSetId,
            accounting_method: accountingMethod,
            show_non_zero_accounts: showNonZeroAccounts,
            view_by: viewBy,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/new/forecast/${queryUrl}`,
        };
      },
      providesTags: ["FORECAST_REPORT"],
    }),

    downloadForecastReport: build.query<
      InteractiveReport,
      {
        groupId: string;
        entityId: string;
        reportType: string;
        startDate: string;
        endDate: string;
        forecastStartDate: string;
        forecastEndDate: string;
        assumptionSetId?: string | null;
        accountingMethod?: string;
        showNonZeroAccounts?: boolean | null;
        action: string;
        folderId?: string;
        exportToMail?: boolean;
        viewBy?: string | null;
      }
    >({
      query: ({
        groupId,
        entityId,
        reportType,
        startDate,
        endDate,
        forecastStartDate,
        forecastEndDate,
        assumptionSetId,
        accountingMethod,
        showNonZeroAccounts,
        action,
        folderId,
        exportToMail,
        viewBy,
      }) => {
        const queryUrl = qs.stringify(
          {
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            forecast_start_date: forecastStartDate,
            forecast_end_date: forecastEndDate,
            assumption_set_id: assumptionSetId,
            accounting_method: accountingMethod,
            show_non_zero_accounts: showNonZeroAccounts,
            action,
            folder_id: folderId,
            export_to_mail: exportToMail,
            view_by: viewBy,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/interactive_report/new/forecast/${queryUrl}`,
        };
      },
    }),

    getForecastSettings: build.query<ForecastSettings[], { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/`,
      }),
      providesTags: ["FORECAST_SETTINGS"],
    }),

    createForecastSettings: build.mutation<
      ForecastSettings,
      {
        entityId: string;
        name: string;
        is_default?: boolean;
        rules?: ForecastRuleInput[];
      }
    >({
      query: ({ entityId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["FORECAST_SETTINGS", "FORECAST_REPORT"],
    }),

    updateForecastSettings: build.mutation<
      ForecastSettings,
      {
        entityId: string;
        settingsId: string;
        name?: string;
        is_default?: boolean;
        rules?: ForecastRuleInput[];
      }
    >({
      query: ({ entityId, settingsId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/${settingsId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["FORECAST_SETTINGS", "FORECAST_REPORT"],
    }),

    deleteForecastSettings: build.mutation<
      void,
      { entityId: string; settingsId: string }
    >({
      query: ({ entityId, settingsId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/${settingsId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["FORECAST_SETTINGS"],
    }),

    upsertForecastRule: build.mutation<
      ForecastRule,
      { entityId: string; settingsId: string } & ForecastRuleInput
    >({
      query: ({ entityId, settingsId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/${settingsId}/rules/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FORECAST_SETTINGS", "FORECAST_REPORT"],
    }),

    deleteForecastRule: build.mutation<
      void,
      { entityId: string; settingsId: string; ruleId: string }
    >({
      query: ({ entityId, settingsId, ruleId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/${settingsId}/rules/${ruleId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["FORECAST_SETTINGS", "FORECAST_REPORT"],
    }),

    updateForecastPlug: build.mutation<
      {
        name: string;
        parent_category_uuid: string | null;
        parent_category_name: string | null;
      },
      {
        entityId: string;
        settingsId: string;
        name?: string;
        parent_category_uuid?: string | null;
        clear_parent?: boolean;
      }
    >({
      query: ({ entityId, settingsId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/forecast-settings/${settingsId}/plug/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["FORECAST_SETTINGS", "FORECAST_REPORT"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllReportsQuery,
  useGenerateReportMutation,
  useGetReportByIdQuery,
  useUpdateReportMutation,
  useDeleteReportFileMutation,
  useNewInteractiveReportQuery,
  useLazyNewInteractiveReportQuery,
  useExportSelectedTransactionsMutation,
  useSaveInteractiveReportMutation,
  useGetAllInteractiveReportsQuery,
  useGetInteractiveReportQuery,
  useDeleteInteractiveReportMutation,
  useUpdateInteractiveReportMutation,
  useGetTransactionsForReportQuery,
  useLazyGetTransactionsForReportQuery,
  useByViewInteractiveReportQuery,
  useLazyByViewInteractiveReportQuery,
  useGetVarianceExplanationQuery,
  useLazyGetVarianceExplanationQuery,
  useGetVarianceExplanationListQuery,
  useExportVarianceExplanationReportMutation,
  useGetAgingSummaryQuery,
  useExportARAgingSummaryMutation,
  usePrefetch: useReportsPrefetch,
  useGetVendorSummaryQuery,
  useLazyExportVendorSummaryQuery,
  useGetAPAgingSummaryQuery,
  useExportAPAgingSummaryMutation,
  useExportConsolidatedReportMutation,
  useGetForecastReportQuery,
  useLazyDownloadForecastReportQuery,
  useGetForecastSettingsQuery,
  useCreateForecastSettingsMutation,
  useUpdateForecastSettingsMutation,
  useDeleteForecastSettingsMutation,
  useUpsertForecastRuleMutation,
  useDeleteForecastRuleMutation,
  useUpdateForecastPlugMutation,
} = extendedApi;
